<template>
  <q-select
    :model-value="modelValue"
    transition-duration="100"
    dense
    filled
    clearable
    :label="label"
    use-input
    input-debounce="0"
    :options="options"
    style="width: 250px"
    input-class="ellipsis"
    @filter="filterUsers"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey"> Could not find user in organization </q-item-section>
      </q-item>
    </template>
    <template #prepend>
      <q-avatar v-if="modelValue?.picture">
        <q-img :src="modelValue?.picture" referrerpolicy="no-referrer" no-spinner />
      </q-avatar>
      <q-icon v-else :name="icon" />
    </template>
    <template #option="opt">
      <q-list>
        <q-item v-bind="opt.itemProps">
          <q-item-section avatar>
            <q-avatar size="30px" round>
              <q-img
                v-if="opt.opt.picture"
                :src="opt.opt.picture"
                referrerpolicy="no-referrer"
                no-spinner
              />
              <q-icon v-else name="person" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label v-if="opt.opt.given_name && opt.opt.family_name">
              {{ opt.opt.given_name }} {{ opt.opt.family_name }}
            </q-item-label>
            <q-item-label v-else>
              {{ opt.opt.email }}
            </q-item-label>
            <q-item-label v-if="opt.opt.given_name && opt.opt.family_name" caption>{{
              opt.opt.email
            }}</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </template>
    <template #selected>
      <div class="text-weight-regular ellipsis" style="max-width: 80px">
        {{ displayText(modelValue) }}
      </div>
    </template>
  </q-select>
</template>

<script lang="ts">
import type { AccessUser } from 'common'
import type { PropType } from 'vue'

export default {
  name: 'SelectUser',
  props: {
    userList: {
      type: Array as PropType<AccessUser[]>,
      required: true
    },
    modelValue: {
      type: [Object, null] as PropType<AccessUser | null>,
      required: true
    },
    label: {
      type: String,
      required: false,
      default: 'Filter by Report Owner'
    },
    icon: {
      type: String,
      required: false,
      default: 'person'
    }
  },
  emits: ['update:model-value'],
  data() {
    return {
      options: [] as AccessUser[]
    }
  },
  methods: {
    sortUsers(users: AccessUser[]) {
      // Sort in alphabetical order
      return users.sort((a, b) => {
        const aName = a.given_name ? `${a.given_name} ${a.family_name}` : a.email
        const bName = b.given_name ? `${b.given_name} ${b.family_name}` : b.email
        return aName.localeCompare(bName)
      })
    },
    filterUsers(val: string, updateFn) {
      // updateFn is emitted by quasar and can be utilized to handle updates on nextTick
      if (val === '') {
        updateFn(() => {
          this.options = this.sortUsers([...this.userList])
        })
        return
      }

      updateFn(() => {
        const needle = val.toLowerCase()
        const filteredUsers = this.userList.filter((user: AccessUser) => {
          return (
            user.email.toLowerCase().includes(needle) ||
            user.given_name?.toLowerCase().includes(needle)
          )
        })
        this.options = this.sortUsers(filteredUsers)
      })
    },
    displayText(user: AccessUser | null): string {
      if (user === null) {
        return ''
      }
      if (user.given_name) {
        return `${user.given_name} ${user.family_name}`
      }
      return `${user.email}`
    }
  }
}
</script>
